import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Route } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { of, switchMap } from 'rxjs';
import { VisitHistoryGridItemObject } from '@libs/types/src/navigator/object-type/visit-history-grid.object';
import { appRoutes } from '../../../../app.routes';
import { contentRoutes } from '../../../content/content.routes';
import { ComponentStoreV2Factory } from '../../../shared/store/common/component-store-v2.factory';
import {
    GroupedVisitHistory,
    ResolvedVisitHistory,
    VisitRouteMetadata,
    VisitRouteNode,
} from './recently-visited.types';

const VARIABLE_ROUTE = '_';

@Injectable()
export class RecentlyVisitedStore extends ComponentStoreV2Factory {
    private readonly translate = inject(TranslateService);
    private readonly routeTree = this.createRouteTree();
    private readonly languageChange = toSignal(this.translate.onLangChange, { initialValue: null });
    private historyRequest = 0;

    public readonly history = signal<VisitHistoryGridItemObject[]>([]);

    public readonly groupedHistory = computed<GroupedVisitHistory[]>(() => {
        this.languageChange();

        const groups: GroupedVisitHistory[] = [];
        let previousRouteKey: string | undefined;
        let previousGroupKey: string | null | undefined;

        this.history().forEach((history, index) => {
            const resolved = this.resolveHistory(history, index);
            if (!resolved) {
                previousRouteKey = undefined;
                previousGroupKey = undefined;
                return;
            }

            const routeKey = resolved.groupKey
                ? `${resolved.groupKey}:${resolved.route.path.join('/')}`
                : resolved.route.fullPath.join('/');
            if (routeKey === previousRouteKey) return;

            const previousGroup = groups.at(-1);
            if (
                resolved.groupKey
                && previousGroupKey === resolved.groupKey
                && previousGroup?.groupKey === resolved.groupKey
            ) {
                previousGroup.routes.push(resolved.route);
            } else {
                groups.push({
                    key: `${resolved.groupKey || resolved.route.url}:${index}`,
                    group: resolved.group,
                    groupKey: resolved.groupKey,
                    routes: [resolved.route],
                });
            }

            previousRouteKey = routeKey;
            previousGroupKey = resolved.groupKey;
        });

        return groups;
    });

    private readonly newHistory = toSignal(
        toObservable(this.globalGlobalStore.loggedUserStore.currentUserId).pipe(
            switchMap(userId => userId
                ? this.api.visitHistorySubscription(userId)
                : of(null),
            ),
            takeUntilDestroyed(),
        ),
        { initialValue: null },
    );

    constructor() {
        super();

        effect(() => {
            const userId = this.globalGlobalStore.loggedUserStore.currentUserId();
            const request = ++this.historyRequest;

            this.history.set([]);
            if (!userId) return;

            void this.loadHistory(userId, request);
        });

        effect(() => {
            const newHistory = this.newHistory();
            if (!newHistory?.url) return;

            this.history.update(history => this.sameHistory(history[0], newHistory)
                ? history
                : [newHistory, ...history],
            );
        });
    }

    private async loadHistory(userId: string, request: number): Promise<void> {
        const history = await this.api.getVisitHistory();
        if (
            request !== this.historyRequest
            || userId !== this.globalGlobalStore.loggedUserStore.currentUserId()
        ) return;

        this.history.update(current => {
            const loaded = history || [];
            const pending = current.filter(item => !item.created);
            let includedPending = 0;

            while (
                includedPending < pending.length
                && this.sameHistory(pending[includedPending], loaded[includedPending])
            ) {
                includedPending++;
            }

            return [...pending.slice(includedPending), ...loaded];
        });
    }

    private resolveHistory(
        history: VisitHistoryGridItemObject,
        index: number,
    ): ResolvedVisitHistory | null {
        if (!history.url) return null;

        const segments = this.urlSegments(history.url);
        const visitData = this.parseVisitData(history.data);
        const fullPath: string[] = [];
        let path: string[] = [];
        let node = this.routeTree;
        let matchedNode: VisitRouteNode | null = null;
        let matchedRoute: VisitRouteMetadata | null = null;
        let group: string | null = null;
        let groupKey: string | null = null;
        let hasRouteMatch = true;

        for (let segmentIndex = 0; segmentIndex < segments.length; segmentIndex++) {
            const segment = segments[segmentIndex];
            if (!hasRouteMatch) {
                const fallback = this.humanize(segment);
                fullPath.push(fallback);
                path.push(fallback);
                continue;
            }

            matchedNode = node.children[segment]
                || node.children[VARIABLE_ROUTE]
                || null;

            if (!matchedNode) {
                hasRouteMatch = false;
                matchedRoute = null;
                const fallback = this.humanize(segment);
                fullPath.push(fallback);
                path.push(fallback);
                continue;
            }

            node = matchedNode;
            matchedRoute = matchedNode.terminal && (
                segmentIndex === segments.length - 1
                || !matchedNode.branchConfigured
            )
                ? matchedNode.terminal
                : matchedNode;
            const label = this.resolveLabel(matchedRoute, segment, visitData[segmentIndex]);

            if (!matchedRoute.skip || matchedRoute.group) {
                fullPath.push(label);
            }
            if (!matchedRoute.skip) {
                path.push(label);
            }

            if (matchedRoute.group) {
                group = label;
                groupKey = `/${segments.slice(0, segmentIndex + 1).join('/')}`;
                path = [];
            }
        }

        if (matchedRoute?.skip) return null;

        const fallbackPath = fullPath.length
            ? fullPath
            : [this.humanize(segments.at(-1) || history.url)];
        const routePath = path.length
            ? path
            : [group || fallbackPath.at(-1) || history.url];

        return {
            group,
            groupKey,
            route: {
                key: `${history.created || index}:${history.url}`,
                url: history.url,
                path: routePath,
                fullPath: fallbackPath,
            },
        };
    }

    private createRouteTree(): VisitRouteNode {
        const root = this.createRouteNode('');

        appRoutes.forEach(route => {
            const children = route.path === 'content' || route.path === 'embed/content'
                ? contentRoutes[0]?.children
                : route.children;

            this.addRoute(root, { ...route, children });
        });

        return root;
    }

    private addRoute(parent: VisitRouteNode, route: Route): void {
        if (route.redirectTo) return;

        const segments = (route.path || '').split('/').filter(Boolean);
        let node = parent;

        segments.forEach((segment, index) => {
            const dynamic = segment.startsWith(':');
            const key = dynamic ? VARIABLE_ROUTE : segment;
            node.children[key] ??= this.createRouteNode(segment, dynamic);
            node = node.children[key];

            if (index === segments.length - 1) {
                const metadata = this.routeMetadata(route, segment, dynamic);
                node.terminal ??= metadata;

                if ((route.children?.length || 0) > 0 && !node.branchConfigured) {
                    node.labelKey = metadata.labelKey;
                    node.dynamic = metadata.dynamic;
                    node.group = metadata.group;
                    node.skip = metadata.skip;
                    node.branchConfigured = true;
                }
            }
        });

        (route.children || []).forEach(child => this.addRoute(node, child));
    }

    private createRouteNode(labelKey: string, dynamic = false): VisitRouteNode {
        return {
            labelKey,
            dynamic,
            group: false,
            skip: false,
            terminal: null,
            branchConfigured: false,
            children: {},
        };
    }

    private routeMetadata(route: Route, fallback: string, dynamic: boolean): VisitRouteMetadata {
        return {
            labelKey: this.routeLabel(route, fallback),
            dynamic,
            group: this.routeFlag(route, 'group'),
            skip: this.routeFlag(route, 'skip'),
        };
    }

    private routeLabel(route: Route, fallback: string): string {
        const breadcrumb: unknown = route.data?.['breadcrumb'];
        if (typeof breadcrumb === 'string') return breadcrumb;
        if (!this.isRecord(breadcrumb)) return fallback;

        const label = breadcrumb['label'];
        if (typeof label === 'string') return label;

        const alias = breadcrumb['alias'];
        return typeof alias === 'string' ? alias : fallback;
    }

    private routeFlag(route: Route, flag: 'group' | 'skip'): boolean {
        const visit: unknown = route.data?.['visit'];
        return this.isRecord(visit) && visit[flag] === true;
    }

    private resolveLabel(route: VisitRouteMetadata, segment: string, visitLabel: unknown): string {
        if (route.dynamic) {
            return typeof visitLabel === 'string' && visitLabel && !visitLabel.startsWith('__')
                ? visitLabel
                : this.humanize(segment);
        }

        const labelKey = route.labelKey || segment;
        const key = labelKey.includes('.') ? labelKey : `breadcrumbs.${labelKey}`;
        const translated: unknown = this.translate.instant(key);
        return typeof translated === 'string' && translated !== key
            ? translated
            : this.humanize(labelKey);
    }

    private parseVisitData(data: string | null): unknown[] {
        if (!data) return [];

        try {
            const parsed: unknown = JSON.parse(data);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    private urlSegments(url: string): string[] {
        return url
            .split(/[?#]/, 1)[0]
            .split('/')
            .filter(Boolean)
            .map(segment => {
                try {
                    return decodeURIComponent(segment);
                } catch {
                    return segment;
                }
            });
    }

    private humanize(value: string): string {
        const normalized = value
            .replace(/^__/, '')
            .replace(/[-_]+/g, ' ')
            .replace(/([a-z])([A-Z])/g, '$1 $2')
            .trim();

        return normalized
            ? normalized.charAt(0).toLocaleUpperCase() + normalized.slice(1)
            : value;
    }

    private sameHistory(
        first: VisitHistoryGridItemObject | undefined,
        second: VisitHistoryGridItemObject | undefined,
    ): boolean {
        return !!first
            && !!second
            && first.url === second.url
            && first.data === second.data;
    }

    private isRecord(value: unknown): value is Record<string, unknown> {
        return typeof value === 'object' && value !== null;
    }
}
